import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const NonWorkingDaysPanel = withListOverflow(NonWorkingDaysListOwner);
// src/features/admin/NonWorkingDaysPanel.jsx
//
// Admin panel for company-wide closures and country-local holidays.
// Calendar settings used by attendance and utilization reporting.

import React, { useState, useEffect, useCallback } from "react";
import { T } from "../../app/tokens.js";
import {
  getNonWorkingDaysAdmin,
  createNonWorkingDay,
  updateNonWorkingDay,
  deleteNonWorkingDay,
} from "../../data/adminClient.js";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function fmtDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return iso || "";
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

function fmtRange(e) {
  return e.endDate && e.endDate !== e.date ? `${fmtDate(e.date)} \u2013 ${fmtDate(e.endDate)}` : fmtDate(e.date);
}

function sortEntries(list) {
  return [...list].sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

const labelStyle = {
  fontSize: T.fsXxxs5, fontWeight: T.fwBold, letterSpacing: 0.3,
  textTransform: "uppercase", color: T.light,
};
const inputStyle = {
  border: `1px solid ${T.borderDark}`, borderRadius: 7, padding: "7px 10px",
  fontSize: T.fsSm5, background: T.white, color: T.dark, fontFamily: "inherit", minWidth: 150,
};
const addBtn = {
  marginLeft: "auto", fontSize: T.fsSm, fontWeight: T.fwBold, color: T.blue,
  background: T.white, border: `1px solid ${T.blueSoftBorder}`, borderRadius: 8,
  padding: "6px 11px", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit",
};
const linkBtn = {
  background: "transparent", border: "none", color: T.blue, fontSize: T.fsSm,
  fontWeight: T.fwBold, cursor: "pointer", padding: "0 6px", fontFamily: "inherit",
};

function Editor({ draft, title, appliesTo, saving, error, onChange, onSave, onCancel }) {
  const id = draft.key;
  return (
    <form
      data-testid="nwd-editor"
      onSubmit={(e) => { e.preventDefault(); onSave(); }}
      style={{
        background: T.selBg, border: `1px solid ${T.selBorder}`, borderRadius: 10,
        padding: "13px 14px", margin: "12px 0 6px",
      }}
    >
      <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: 0.4, textTransform: "uppercase", color: T.blue, marginBottom: 9 }}>
        {title}
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label htmlFor={`${id}-date`} style={labelStyle}>Start date</label>
          <input id={`${id}-date`} type="date" style={{ ...inputStyle, minWidth: 130 }} value={draft.date}
            onChange={(e) => onChange({ date: e.target.value })} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label htmlFor={`${id}-end`} style={labelStyle}>End date (optional)</label>
          <input id={`${id}-end`} type="date" style={{ ...inputStyle, minWidth: 130 }} value={draft.endDate}
            onChange={(e) => onChange({ endDate: e.target.value })} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label htmlFor={`${id}-name`} style={labelStyle}>Name</label>
          <input id={`${id}-name`} type="text" style={inputStyle} value={draft.name}
            onChange={(e) => onChange({ name: e.target.value })} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label htmlFor={`${id}-applies`} style={labelStyle}>Applies to</label>
          <input id={`${id}-applies`} type="text" style={inputStyle} value={appliesTo} readOnly />
        </div>
        <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
          <button type="button" onClick={onCancel} disabled={saving}
            style={{ background: T.white, color: T.mid, fontWeight: T.fwBold, border: `1px solid ${T.border}`, borderRadius: 7, padding: "8px 14px", fontSize: T.fsSm, cursor: "pointer", fontFamily: "inherit" }}>
            Cancel
          </button>
          <button type="submit" disabled={saving}
            style={{ background: T.blue, color: T.white, fontWeight: T.fwBold, border: 0, borderRadius: 7, padding: "8px 15px", fontSize: T.fsSm, cursor: "pointer", opacity: saving ? 0.6 : 1, fontFamily: "inherit" }}>
            {saving ? "Saving\u2026" : "Save"}
          </button>
        </div>
      </div>
      {error && <div role="alert" style={{ color: T.red, fontSize: T.fsSm, marginTop: 8 }}>{error}</div>}
    </form>
  );
}

function EntryList({ entries, editingId, onEdit, onRemove, emptyText, renderEditor }) {
  if (entries.length === 0) {
    return (
      <div style={{ padding: "14px 12px", border: `1px dashed ${T.borderDark}`, borderRadius: 8, color: T.mid, fontSize: T.fsBase }}>
        {emptyText}
      </div>
    );
  }
  return (
    <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", minWidth: 550, borderCollapse: "collapse" }}>
      <thead><tr>{["Dates", "Name", "Actions"].map((label) => <th key={label}
        scope="col" style={{ textAlign: "left", padding: "7px 4px", color: T.mid, background: T.tint, fontSize: T.fsXs }}>{label}</th>)}</tr></thead>
      <tbody>
        {entries.map((e) => {
          const isRange = e.endDate && e.endDate !== e.date;
          return (
            <React.Fragment key={e.id}>
              <tr data-testid={`nwd-row-${e.id}`} style={{ background: isRange ? T.blueXLight : "transparent" }}>
                <td style={{ padding: "7px 4px", borderBottom: `1px solid ${T.lightGray}`, fontWeight: T.fwBold, whiteSpace: "nowrap", width: 210, color: T.dark, fontSize: T.fsSm5 }}>
                  {fmtRange(e)}
                </td>
                <td style={{ padding: "7px 4px", borderBottom: `1px solid ${T.lightGray}`, color: T.dark, fontSize: T.fsSm5 }}>
                  {e.name}
                </td>
                <td style={{ padding: "7px 4px", borderBottom: `1px solid ${T.lightGray}`, textAlign: "right", whiteSpace: "nowrap" }}>
                  <button type="button" style={linkBtn} aria-label={`Edit ${e.name}`} onClick={() => onEdit(e)}>Edit</button>
                  <button type="button" style={{ ...linkBtn, color: T.red }} aria-label={`Remove ${e.name}`} onClick={() => onRemove(e)}>Remove</button>
                </td>
              </tr>
              {editingId === e.id && (
                <tr><td colSpan={3}>{renderEditor()}</td></tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}

function NonWorkingDaysListOwner({ explainer } = {}) {
  const [entries, setEntries] = useState([]);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editor, setEditor] = useState(null); // { mode, scope, id?, key, date, endDate, name }
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    getNonWorkingDaysAdmin()
      .then((data) => {
        const list = Array.isArray(data?.entries) ? data.entries : [];
        const cs = Array.isArray(data?.countries) ? data.countries : [];
        setEntries(list);
        setCountries(cs);
        setCountry((prev) => (prev && cs.includes(prev) ? prev : cs[0] || ""));
      })
      .catch((e) => setLoadError(e?.message || "Could not load non-working days."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const wide = sortEntries(entries.filter((e) => e.scope === "ripple_wide"));
  const local = sortEntries(entries.filter((e) => e.scope === "country" && e.country === country));

  function openAdd(scope) {
    setSaveError(null);
    setEditor({ mode: "add", scope, key: `nwd-${scope}-new`, date: "", endDate: "", name: "" });
  }
  function openEdit(e) {
    setSaveError(null);
    setEditor({
      mode: "edit", scope: e.scope, id: e.id,
      key: `nwd-edit-${e.id}`, date: e.date || "", endDate: e.endDate && e.endDate !== e.date ? e.endDate : "", name: e.name || "",
    });
  }

  async function save() {
    if (!editor.date || !editor.name.trim()) {
      setSaveError("Start date and name are required.");
      return;
    }
    if (editor.endDate && editor.endDate < editor.date) {
      setSaveError("End date must not be before the start date.");
      return;
    }
    const isCountry = editor.scope === "country";
    const payload = {
      scope: isCountry ? "country" : "ripple_wide",
      country: isCountry ? (editor.mode === "edit" ? entries.find((x) => x.id === editor.id)?.country ?? country : country) : null,
      date: editor.date,
      endDate: editor.endDate || null,
      name: editor.name.trim(),
    };
    setSaving(true);
    setSaveError(null);
    try {
      if (editor.mode === "edit") await updateNonWorkingDay(editor.id, payload);
      else await createNonWorkingDay(payload);
      setEditor(null);
      load();
    } catch (e) {
      setSaveError(e?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(e) {
    setActionError(null);
    try {
      await deleteNonWorkingDay(e.id);
      setEntries((prev) => prev.filter((x) => x.id !== e.id));
      if (editor?.id === e.id) setEditor(null);
    } catch (err) {
      setActionError(err?.message || "Could not remove.");
    }
  }

  const patch = (p) => setEditor((prev) => ({ ...prev, ...p }));
  const editorFor = (scope) => () => (
    <Editor
      draft={editor}
      title={`${editor.mode === "edit" ? "Edit" : "Add"} ${scope === "country" ? `holiday \u00b7 ${country}` : "day \u00b7 Ripple-wide"}`}
      appliesTo={scope === "country" ? `${country} offices` : "All offices"}
      saving={saving}
      error={saveError}
      onChange={patch}
      onSave={save}
      onCancel={() => setEditor(null)}
    />
  );

  const card = {
    background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
    padding: "16px 18px", boxShadow: T.cardShadow, marginBottom: 16,
  };
  const pill = (bg, fg) => ({
    fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: 0.4, textTransform: "uppercase",
    borderRadius: 5, padding: "2px 8px", background: bg, color: fg,
  });

  if (loading) {
    return (
      <div data-testid="non-working-days-panel">
        <h2 style={{ fontSize: T.fsMd5 + 4, margin: "0 0 3px", color: T.hdrFrom }}>Non-working days</h2>
        {explainer}
        <div data-testid="nwd-loading" style={{ color: T.mid, fontSize: T.fsMd }}>Loading non-working days{"\u2026"}</div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div data-testid="non-working-days-panel">
        <h2 style={{ fontSize: T.fsMd5 + 4, margin: "0 0 3px", color: T.hdrFrom }}>Non-working days</h2>
        {explainer}
        <div role="alert" style={{ background: T.alertErrBg, border: `1px solid ${T.alertErrBorder}`, borderRadius: 8, padding: 14, color: T.red, fontSize: T.fsBase }}>
          {loadError}{" "}
          <button type="button" onClick={load} style={{ ...linkBtn, color: T.red, textDecoration: "underline" }}>Retry</button>
        </div>
      </div>
    );
  }

  const otherCountries = countries.filter((c) => c !== country);
  const countriesWithoutLocalEntries = countries.filter(
    (c) => !entries.some((entry) => entry.scope === "country" && entry.country === c),
  );

  return (
    <div data-testid="non-working-days-panel">
      <h2 style={{ fontSize: T.fsMd5 + 4, margin: "0 0 3px", color: T.hdrFrom }}>Non-working days</h2>
      {explainer}
      {actionError && <div role="alert" style={{ color: T.red, fontSize: T.fsSm, marginBottom: 10 }}>{actionError}</div>}

      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.hdrFrom }}>Ripple-wide</span>
          <span style={pill(T.blueSoft, T.blue)}>All offices</span>
          <button type="button" style={addBtn} onClick={() => openAdd("ripple_wide")}>+ Add day</button>
        </div>
        <div style={{ fontSize: T.fsXs5, color: T.light, marginBottom: 12 }}>
          Applies to every office worldwide. Countries don{"'"}t re-list these.
        </div>
        <EntryList
          entries={wide} editingId={editor?.scope === "ripple_wide" && editor.mode === "edit" ? editor.id : null}
          onEdit={openEdit} onRemove={remove}
          emptyText="No Ripple-wide closures yet. Add the first one."
          renderEditor={editorFor("ripple_wide")}
        />
        {editor?.scope === "ripple_wide" && editor.mode === "add" && editorFor("ripple_wide")()}
      </div>

      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.hdrFrom }}>By country</span>
          <span style={pill(T.ragGreenBg, T.greenDeep)}>Local extras</span>
        </div>
        <div style={{ fontSize: T.fsXs5, color: T.light, marginBottom: 12 }}>
          Statutory holidays for each market country, on top of the Ripple-wide list above.
        </div>
        {countries.length > 0 && <div data-testid="nwd-country-coverage" style={{
          background: T.selBg, border: `1px solid ${T.selBorder}`, borderRadius: 8,
          padding: "9px 11px", marginBottom: 12, color: T.mid, fontSize: T.fsSm5, lineHeight: 1.45,
        }}>
          {countriesWithoutLocalEntries.length
            ? <>No country-specific calendar entries yet for: <strong>{countriesWithoutLocalEntries.join(", ")}</strong>. Ripple-wide dates above still apply to every office.</>
            : <>Every office country has at least one country-specific calendar entry. Ripple-wide dates above still apply to every office.</>}
        </div>}
        {countries.length === 0 ? (
          <div style={{ color: T.mid, fontSize: T.fsBase }}>No countries found in the office inventory.</div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
              <select
                aria-label="Country"
                value={country}
                onChange={(e) => { setCountry(e.target.value); setEditor((p) => (p?.scope === "country" ? null : p)); }}
                style={{ ...inputStyle, fontWeight: T.fwBold, color: T.hdrFrom, minWidth: 210 }}
              >
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button type="button" style={addBtn} onClick={() => openAdd("country")}>
                + Add holiday for {country}
              </button>
              {otherCountries.length > 0 && (
                <span style={{ width: "100%", fontSize: T.fsXxs5, color: T.light }}>
                  <b style={{ color: T.mid }}>Other countries:</b> {otherCountries.join(" \u00b7 ")}
                </span>
              )}
            </div>
            <EntryList
              entries={local} editingId={editor?.scope === "country" && editor.mode === "edit" ? editor.id : null}
              onEdit={openEdit} onRemove={remove}
              emptyText={`No holidays seeded for ${country} yet. Add the first one to start its local list.`}
              renderEditor={editorFor("country")}
            />
            {editor?.scope === "country" && editor.mode === "add" && editorFor("country")()}
          </>
        )}
      </div>
    </div>
  );
}

export default NonWorkingDaysPanel;
